<template>
    <div class="relative">
        <div v-if="loading" class="absolute-center z-10">
            <font-awesome-icon
                icon="spinner"
                spin
                pulse
                size="2x"
            />
        </div>
        <div
            :class="{'opacity-25': loading}"
        >
            <div class="relative">
                <base-map
                    ref="baseMap"
                    :center="center"
                    :markers="markers"
                    :zoom="zoom"
                    map-height="50vh"
                >
                    <gmap-marker
                        v-for="(marker, index) in markers"
                        :key="index"
                        :icon="markerIcon(marker)"
                        :position="markerPosition(marker)"
                        :clickable="true"
                        :draggable="false"
                        @click="markerClicked(marker)"
                    />
                </base-map>
                <div
                    v-if="activeMarkerObject.open"
                    class="absolute pin-l pin-r mx-auto p-lg bg-white text-center"
                    style="width: 250px; top: 20%;"
                >
                    <div
                        class="absolute pin-t pin-r mt-sm mr-sm cursor-pointer"
                        @click="activeMarkerObject.open = false"
                    >
                        <font-awesome-icon icon="times" />
                    </div>

                    <p class="text-lg">{{ activeMarkerObject.marker.street_name }} {{ activeMarkerObject.marker.street_number }}</p>
                    <button
                        class="btn btn-md is-primary mt-md"
                        @click="selectAddress(activeMarkerObject.marker)"
                    >
                        <span v-if="activeMarkerIsSelected">
                            Markera adress
                        </span>
                        <span v-else>
                            Avmarkera adress
                        </span>
                    </button>
                </div>
            </div>

            <div class="flex fixed w-full pin-b">
                <div class="w-1/2 overflow-y-scroll flex p-0 border-r border-neutral-dark bg-white" style="height: 39vh;">
                    <street-list
                        v-if="hasStreets"
                        :streets="streets"
                        :addresses-count="addressesCount"
                        :selected-addresses="selectedAddresses"
                        :assigned-addresses="assignedAddresses"
                    />
                    <div v-else class="p-lg border-b border-neutral-light">
                        <div class="flex justify-start">
                            <span class="text-md">
                                Det finns inga otilldelade adresser.
                            </span>
                        </div>
                    </div>
                </div>
                <div class="flex p-0 bg-white w-1/2 overflow-y-scroll" style="height: 39vh;">
                    <seller-list
                        :team="team"
                        :project="project"
                        :selected-seller="selectedSeller"
                        :sellers="sellers"
                        :selected-addresses="selectedAddresses"
                        :is-field-view="isFieldView"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { markerIcon } from '../../../../mixins/MapMarkerIcon';
    import Coordinates from '../../../Maps/Coordinates';
    import Drawing from '../../../Maps/Drawing';
    import SellerList from './Sellers/SellerList';
    import StreetList from './Streets/StreetList';

    export default {
        components: { SellerList, StreetList },
        props: {
            project: {
                type: Object,
                required: true,
            },
            salesPeriod: {
                type: Object,
                required: true,
            },
            isFieldView: {
                type: Boolean,
                required: false,
                default: false,
            },
            canDraw: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                center: window.defaultMapCenter,
                zoom: 11,
                markers: [],
                streets: {},
                addressesCount: 0,
                selectedSeller: null,
                selectedAddresses: [],
                assignedAddresses: [],
                team: {},
                sellers: {},
                loading: false,
                mapLoaded: false,
                currentMarkerIndex: null,
                activeMarkerObject: {
                    marker: null,
                    open: false,
                },
                drawingInitialized: false,
                drawingManager: null,
            };
        },

        computed: {
            activeMarkerIsSelected() {
                return this.findAddressIndex(this.activeMarkerObject.marker);
            },

            hasStreets() {
                return Object.keys(this.streets).length > 0;
            },
        },

        watch: {
            project() {
                // Get new data if the props project & salesPeriod updates
                if (this.project && this.salesPeriod && this.salesPeriod.project_id === this.project.id) {
                    // Reset selected props
                    this.selectedSeller = null;
                    this.selectedAddresses = [];

                    // Re-fetch addresses and sellers
                    this.getAddressesAndSellers();
                }
            },

            markers() {
                if (this.markers.length === 0) {
                    this.setProjectCenter();
                }
            },
        },

        created() {
            this.getAddressesAndSellers();

            EventBus.$on('select-seller', (seller, checked) => {
                this.selectedSeller = null;
                if (checked) {
                    this.selectedSeller = seller;
                }
            });
            EventBus.$on('update-selected-street', this.updateSelectedStreet);
            EventBus.$on('select-address', this.selectAddress);
            EventBus.$on('toggle-addresses-selection', this.toggleAddressesSelection);
            EventBus.$on('un-assign-address', this.unAssignAddress);
            EventBus.$on('assign-addresses', this.assignAddresses);

            this.$nextTick(() => {
                this.$refs.baseMap.$refs.map.$mapPromise.then(() => {
                    this.drawingManager = new Drawing(this.$refs.baseMap.$refs.map.$mapObject, {
                        drawingControlOptions: {
                            position: google.maps.ControlPosition.TOP_CENTER,
                            drawingModes: [
                                google.maps.drawing.OverlayType.POLYGON,
                            ],
                        },
                    });
                    this.mapLoaded = true;
                    this.setProjectCenter();
                    this.drawShapes();
                });
            });
        },

        methods: {
            markerIcon,

            markerPosition(marker) {
                return {
                    lat: parseFloat(marker.lat),
                    lng: parseFloat(marker.lng),
                };
            },

            toggleAddressesSelection(selectAll) {
                this.selectedAddresses = [];
                if (selectAll) {
                    for (const street in this.streets) {
                        this.streets[street].forEach(address => {
                            this.selectedAddresses.push(address);
                        });
                    }
                }
            },

            updateSelectedStreet(addresses, isSelected) {
                addresses.forEach(address => {
                    const index = this.findAddressIndex(address);
                    if (! isSelected) {
                        this.selectedAddresses.splice(index, 1);
                    } else if (index < 0 && isSelected) {
                        this.selectedAddresses.push(address);
                    }
                });
            },

            selectAddress(address) {
                const index = this.findAddressIndex(address);
                if (index < 0) {
                    this.selectedAddresses.push(address);
                } else {
                    this.selectedAddresses.splice(index, 1);
                }
            },

            findAddressIndex(address) {
                return this.selectedAddresses.findIndex(selectedAddress => selectedAddress.id === address.id);
            },

            setAssignedAddresses() {
                this.assignedAddresses = [];

                this.sellers.forEach(seller => {
                    seller.sales_period_addresses.forEach(address => {
                        this.assignedAddresses.push(address.address_id);
                    });
                });
            },

            postRequestCallback({ data }) {
                [
                    'markers',
                    'streets',
                    'addressesCount',
                    'team',
                    'sellers',
                ].forEach(item => {
                    if (item in data) {
                        this[item] = data[item];
                    }
                });

                this.selectedAddresses = [];

                this.setAssignedAddresses();

                this.loading = false;
            },

            getAddressesAndSellers() {
                this.loading = true;

                axios.get(route('api.sales-projects.sales-periods.get-addresses-and-sellers', [this.project, this.salesPeriod]))
                    .then(this.postRequestCallback)
                    .catch(this.handleError);
            },

            unAssignAddress(salesPeriodAddress) {
                this.loading = true;

                axios.post(route('api.sales-projects.sales-periods.un-assign-address', [this.project, this.salesPeriod, salesPeriodAddress]))
                    .then(this.postRequestCallback)
                    .catch(this.handleError);
            },

            assignAddresses() {
                this.loading = true;

                const addressIds = this.selectedAddresses.map(address => address['id']);
                const sellerId = this.selectedSeller.id;
                if (addressIds.length === 0 || ! sellerId) {
                    return;
                }

                const payload = {
                    addressIds,
                    sellerId,
                };

                axios.post(route('api.sales-projects.sales-periods.assign-addresses', [this.project, this.salesPeriod]), payload)
                    .then(this.postRequestCallback)
                    .catch(this.handleError);
            },

            handleError(error) {
                this.loading = false;
                let errorMessage = 'Något gick tyvärr fel';
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                flash_error(errorMessage);
            },

            markerClicked(marker) {
                this.$refs.baseMap.filtered = marker;

                if (this.activeMarkerObject.marker && this.activeMarkerObject.marker.id === marker.id) {
                    this.activeMarkerObject.open = ! this.activeMarkerObject.open;

                    return;
                }

                this.activeMarkerObject.open = true;
                this.activeMarkerObject.marker = marker;
            },

            setProjectCenter() {
                if (! this.project || ! this.project.area || ! this.mapLoaded) {
                    return;
                }

                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode(
                    { address: this.project.area },
                    (results, status) => {
                        if (status === 'OK' && results[0]) {
                            const latLng = results[0].geometry.location;
                            this.center = {
                                lat: latLng.lat(),
                                lng: latLng.lng(),
                            };
                        }
                    },
                );
            },

            drawShapes() {
                if (! this.mapLoaded) {
                    return;
                }

                if (! this.coordinates) {
                    this.coordinates = new Coordinates();
                }

                if (this.canDraw && ! this.drawingInitialized) {
                    this.drawingInitialized = true;
                    this.drawingManager.initializeDrawing();
                    this.drawingManager.finishedDrawing((shape) => {
                        const coordinates = this.coordinates.getCoordinatesFromShape(shape);
                        const polygon = this.coordinates.getStringifiedCoordinates(coordinates);
                        EventBus.$emit('shape-drawn', polygon, shape);

                        this.selectAddressesByPolygon(shape);
                    });
                }
            },

            selectAddressesByPolygon(shape) {
                this.selectedAddresses = [];

                this.markers.forEach(marker => {
                    const latLng = this.markerPosition(marker);

                    const isWithinPolygon = google.maps.geometry.poly.containsLocation(latLng, shape);

                    if (isWithinPolygon) {
                        this.selectAddress(marker);
                    }
                });

                setTimeout(() => {
                    shape.setMap(null);
                }, 300);
            },
        },
    };
</script>
